import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Icon, Label, Loader, Message, Modal } from 'semantic-ui-react';
import { API, showSuccess } from '../helpers';
import './ModelChannelPriorityModal.css';

const normalizeChannel = (channel) => ({
  id: channel?.id,
  name: channel?.name || '',
  status: channel?.status,
  groups: Array.isArray(channel?.groups)
    ? channel.groups
    : typeof channel?.groups === 'string'
    ? channel.groups
        .split(',')
        .map((group) => group.trim())
        .filter(Boolean)
    : [],
});

const isEnabledChannel = (status) =>
  status === 1 || status === true || status === 'enabled';
const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;
const sortableId = (model, channelId) =>
  `${encodeURIComponent(model)}::${channelId}`;

export const normalizePriorityResponse = (payload) => {
  const responseData = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const rawConfig = responseData?.config ?? responseData ?? {};
  const rawOrders = rawConfig?.model_orders || {};
  return {
    config: {
      enabled: rawConfig?.enabled === true,
      scope: rawConfig?.scope === 'enabled' ? 'enabled' : 'all',
      model_enabled: rawConfig?.model_enabled || {},
      model_orders: Object.fromEntries(
        Object.entries(rawOrders).map(([model, groups]) => {
          const normalized = Array.isArray(groups) ? groups : [];
          return [
            model,
            normalized.length > 0 && Array.isArray(normalized[0])
              ? normalized
              : normalized.map((id) => [id]),
          ];
        })
      ),
    },
    models: (Array.isArray(responseData?.models) ? responseData.models : [])
      .filter((item) => item && typeof item.model === 'string')
      .map((item) => ({
        model: item.model,
        channels: Array.isArray(item.channels)
          ? item.channels
              .map(normalizeChannel)
              .filter((channel) => channel.id != null)
          : [],
      })),
  };
};

export const mergeModelOrders = (models, configuredOrders = {}) => {
  const drafts = {};
  models.forEach(({ model, channels }) => {
    const byId = new Map(
      channels.map((channel) => [String(channel.id), channel])
    );
    const configuredIds = new Set();
    const ordered = [];
    const groups = Array.isArray(configuredOrders[model])
      ? configuredOrders[model]
      : [];
    groups.forEach((group) => {
      const existing = (Array.isArray(group) ? group : [group])
        .map((id) => byId.get(String(id)))
        .filter(Boolean);
      existing.forEach((channel, index) => {
        configuredIds.add(String(channel.id));
        ordered.push({
          ...channel,
          samePriorityAsPrevious: index > 0,
        });
      });
    });
    drafts[model] = [
      ...ordered,
      ...channels
        .filter((channel) => !configuredIds.has(String(channel.id)))
        .map((channel) => ({
          ...channel,
          samePriorityAsPrevious: false,
        })),
    ];
  });
  return drafts;
};

export const toggleChannelPriorityGroup = (channels, channelId) =>
  channels.map((channel, index) =>
    index > 0 && channel.id === channelId
      ? {
          ...channel,
          samePriorityAsPrevious: !channel.samePriorityAsPrevious,
        }
      : channel
  );

export const getInitialModelEnabled = (models, config = {}) => {
  const configured = config.model_enabled || {};
  return Object.fromEntries(
    models.map(({ model }) => [
      model,
      Object.prototype.hasOwnProperty.call(configured, model)
        ? configured[model] === true
        : config.enabled === true &&
          Object.prototype.hasOwnProperty.call(
            config.model_orders || {},
            model
          ),
    ])
  );
};

export const groupPriorityChannels = (channels) =>
  channels.reduce((groups, channel) => {
    if (channel.samePriorityAsPrevious && groups.length > 0) {
      groups[groups.length - 1].push(channel);
    } else {
      groups.push([channel]);
    }
    return groups;
  }, []);

export const buildPriorityPayload = (modelEnabled, drafts) => {
  const normalizedModelEnabled = Object.fromEntries(
    Object.entries(modelEnabled).map(([model, value]) => [
      model,
      value === true,
    ])
  );
  return {
    enabled: Object.values(normalizedModelEnabled).some(Boolean),
    scope: 'enabled',
    model_enabled: normalizedModelEnabled,
    model_orders: Object.fromEntries(
      Object.entries(drafts).map(([model, channels]) => [
        model,
        groupPriorityChannels(channels)
          .map((group) =>
            group
              .filter((channel) => isEnabledChannel(channel.status))
              .map((channel) => channel.id)
          )
          .filter((group) => group.length > 0),
      ])
    ),
  };
};

const SortableChannel = ({
  channel,
  model,
  position,
  canSharePriority,
  onTogglePriorityGroup,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId(model, channel.id) });
  return (
    <div
      ref={setNodeRef}
      className={`mp-channel${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        type='button'
        className='mp-channel__handle'
        aria-label={t('channel.model_priority.drag_channel', {
          name: channel.name,
        })}
        {...attributes}
        {...listeners}
      >
        <Icon name='bars' />
      </button>
      <span className='mp-channel__pos'>{position}</span>
      <div className='mp-channel__body'>
        <div className='mp-channel__name'>
          <strong>{channel.name || `#${channel.id}`}</strong>
          <span>#{channel.id}</span>
          {position === 1 && (
            <Label className='mp-channel__best' color='blue' size='tiny'>
              {t('channel.model_priority.preferred')}
            </Label>
          )}
        </div>
        <div className='mp-channel__tags'>
          <Label size='mini' basic color='green'>
            <Icon name='check circle' />
            {t('channel.table.status_enabled')}
          </Label>
          {(channel.groups.length
            ? channel.groups
            : [t('channel.model_priority.no_groups')]
          ).map((group) => (
            <Label key={group} size='mini' basic>
              {group}
            </Label>
          ))}
        </div>
      </div>
      {canSharePriority && (
        <button
          type='button'
          className={`mp-channel__group${
            channel.samePriorityAsPrevious ? ' is-linked' : ''
          }`}
          aria-pressed={channel.samePriorityAsPrevious === true}
          aria-label={t(
            channel.samePriorityAsPrevious
              ? 'channel.model_priority.separate_priority'
              : 'channel.model_priority.share_previous_priority'
          )}
          title={t(
            channel.samePriorityAsPrevious
              ? 'channel.model_priority.separate_priority'
              : 'channel.model_priority.share_previous_priority'
          )}
          onClick={onTogglePriorityGroup}
        >
          <Icon name={channel.samePriorityAsPrevious ? 'unlink' : 'linkify'} />
        </button>
      )}
    </div>
  );
};

const ModelChannelPriorityModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [modelEnabled, setModelEnabled] = useState({});
  const [selectedModel, setSelectedModel] = useState('');
  const [query, setQuery] = useState('');
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await API.get('/api/model-channel-priority/config');
      if (response.data?.success === false)
        throw new Error(
          response.data.message || t('channel.model_priority.load_failed')
        );
      const normalized = normalizePriorityResponse(response);
      setModels(normalized.models);
      setDrafts(
        mergeModelOrders(normalized.models, normalized.config.model_orders)
      );
      setModelEnabled(
        getInitialModelEnabled(normalized.models, normalized.config)
      );
      setSelectedModel((current) =>
        normalized.models.some((item) => item.model === current)
          ? current
          : normalized.models[0]?.model || ''
      );
    } catch (loadError) {
      setError(
        getErrorMessage(loadError, t('channel.model_priority.load_failed'))
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) loadConfig();
  }, [open, loadConfig]);
  const visibleModels = useMemo(
    () =>
      models.filter(({ model }) =>
        model.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [models, query]
  );
  const currentChannels = (drafts[selectedModel] || []).filter((channel) =>
    isEnabledChannel(channel.status)
  );
  const currentModelOn = modelEnabled[selectedModel] === true;
  let currentPriority = 0;
  const displayedChannels = currentChannels.map((channel, index) => {
    if (index === 0 || !channel.samePriorityAsPrevious) {
      currentPriority += 1;
    }
    return { channel, position: currentPriority };
  });

  const handleTogglePriorityGroup = (channelId) => {
    setDrafts((current) => ({
      ...current,
      [selectedModel]: toggleChannelPriorityGroup(
        current[selectedModel] || [],
        channelId
      ),
    }));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setDrafts((current) => {
      const channels = current[selectedModel] || [];
      const oldId = Number(String(active.id).split('::').pop());
      const newId = Number(String(over.id).split('::').pop());
      const oldIndex = channels.findIndex((channel) => channel.id === oldId);
      const newIndex = channels.findIndex((channel) => channel.id === newId);
      return oldIndex < 0 || newIndex < 0
        ? current
        : {
            ...current,
            [selectedModel]: arrayMove(channels, oldIndex, newIndex),
          };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await API.put(
        '/api/model-channel-priority/config',
        buildPriorityPayload(modelEnabled, drafts)
      );
      if (response.data?.success === false)
        throw new Error(
          response.data.message || t('channel.model_priority.save_failed')
        );
      showSuccess(t('channel.model_priority.save_success'));
      onClose();
    } catch (saveError) {
      setError(
        getErrorMessage(saveError, t('channel.model_priority.save_failed'))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      closeIcon={!saving}
      size='large'
      className='model-priority-modal'
    >
      <Modal.Header>
        <Icon name='sort amount down' />
        {t('channel.model_priority.title')}
      </Modal.Header>
      <Modal.Content scrolling>
        {loading ? (
          <div className='model-priority-state'>
            <Loader active inline='centered' />
            <span>{t('channel.model_priority.loading')}</span>
          </div>
        ) : (
          <div className='model-priority-layout model-priority-layout--split'>
            {error && (
              <Message negative onDismiss={() => setError('')}>
                <Message.Header>
                  {t('channel.model_priority.error_title')}
                </Message.Header>
                <p>{error}</p>
              </Message>
            )}
            <aside className='model-priority-models'>
              <strong>{t('channel.model_priority.models')}</strong>
              <div className='model-priority-search'>
                <Icon name='search' />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('channel.model_priority.search_model')}
                />
              </div>
              <div className='model-priority-model-list'>
                {visibleModels.map(({ model, channels }) => {
                  const enabledCount = channels.filter((channel) =>
                    isEnabledChannel(channel.status)
                  ).length;
                  return (
                    <button
                      type='button'
                      key={model}
                      className={`model-priority-model-item${
                        selectedModel === model ? ' is-selected' : ''
                      }`}
                      onClick={() => setSelectedModel(model)}
                    >
                      <span className='model-priority-model-item__name'>
                        {model}
                      </span>
                      <span className='model-priority-model-item__meta'>
                        <i className={modelEnabled[model] ? 'is-on' : ''} />
                        {enabledCount}/{channels.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>
            <section className='model-priority-editor'>
              {selectedModel ? (
                <>
                  <div className='model-priority-editor-head'>
                    <div>
                      <strong>{selectedModel}</strong>
                      <p>
                        {currentModelOn
                          ? t('channel.model_priority.model_on_hint')
                          : t('channel.model_priority.model_off_hint')}
                      </p>
                    </div>
                    <button
                      type='button'
                      className={`mp-toggle mp-toggle--sm ${
                        currentModelOn ? 'mp-toggle--on' : ''
                      }`}
                      aria-pressed={currentModelOn}
                      onClick={() =>
                        setModelEnabled((current) => ({
                          ...current,
                          [selectedModel]: !currentModelOn,
                        }))
                      }
                    >
                      <span className='mp-toggle__knob' />
                    </button>
                  </div>
                  {!currentModelOn && (
                    <div className='mp-hint mp-hint--info'>
                      <Icon name='info circle' />
                      {t('channel.model_priority.draft_hint')}
                    </div>
                  )}
                  <div className='mp-list-head'>
                    <div>
                      <strong>
                        {t('channel.model_priority.channel_order')}
                      </strong>
                      <span>
                        {t('channel.model_priority.channel_order_hint')}
                      </span>
                    </div>
                    <Label basic color='blue' size='small'>
                      {currentChannels.length}
                    </Label>
                  </div>
                  {currentChannels.length === 0 ? (
                    <div className='mp-empty'>
                      <Icon name='unlinkify' size='large' />
                      <span>
                        {t('channel.model_priority.no_enabled_channels')}
                      </span>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={currentChannels.map((channel) =>
                          sortableId(selectedModel, channel.id)
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className='mp-list'>
                          {displayedChannels.map(
                            ({ channel, position }, index) => (
                              <SortableChannel
                                key={channel.id}
                                channel={channel}
                                model={selectedModel}
                                position={position}
                                canSharePriority={index > 0}
                                onTogglePriorityGroup={() =>
                                  handleTogglePriorityGroup(channel.id)
                                }
                              />
                            )
                          )}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </>
              ) : (
                <div className='mp-empty'>
                  <Icon name='cube' size='large' />
                  <span>{t('channel.model_priority.no_models')}</span>
                </div>
              )}
            </section>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions className='mp-actions'>
        <span className='model-priority-save-hint'>
          {t('channel.model_priority.save_hint')}
        </span>
        <Button onClick={onClose} disabled={saving}>
          {t('channel.model_priority.cancel')}
        </Button>
        <Button
          primary
          onClick={handleSave}
          loading={saving}
          disabled={loading || saving || models.length === 0}
        >
          <Icon name='save' />
          {t('channel.model_priority.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModelChannelPriorityModal;
